import React from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { AutoComplete } from 'primereact/autocomplete';
import acordLogo from 'images/acordlogo.png';
import { useSelectedClient } from '../../../../hooks/useSelectedClient';
import { resolveCarrierIdByName, resolveCarrierById, lookupCarrierIdSync } from '../InsurerSearchDropdown';
import InsurerNaicField from '../InsurerNaicField';
import { createMachineOwned } from '../../utils/machineOwned';
import CreateNewCarriers from '../CreateNewCarriers';
import AddNewSignature from '../AddNewSignature';
import { useSignatures } from '../../../../hooks/useSignatures';
import ProducerSection, { type ProducerLocation, fetchFirstPolicyAgentContact, fetchAgentContact } from '../ProducerSection';
import { mapFields, mapFieldsAtIndex, getInsurers, getAllRemarkLines, getPolicyAddresses, pickPolicyAddress } from '../acordFieldEngine';
import type { AcordFieldItem } from '../acordFieldEngine';
import { CGL_MAP, AUTO_MAP, UMBRELLA_MAP, WC_MAP, OTHER_MAP } from './acord25FieldMap';
import { parseAcord25StoredCoverages } from '../../utils/parseAcord25StoredCoverages';
import type { StoredCoverage, StoredPolicy } from '../../utils/parseAcord25StoredCoverages';
import type { Acord25FormData } from '../../utils/acord25Payload';

import { formatUsPhone } from 'utilities/formatPhone';

export interface Acord25FormRef {
  getFormData(): Acord25FormData;
}

interface Acord25FormProps {
  showAcord101?: boolean;
  selectedPolicies?: string[];
  /** "Additional Named Insured(s): ..." text derived from selected policies' customFields — auto-fills the ACORD 101 remarks textarea */
  acord101AdditionalNamedInsureds?: string;
  /** True when any selected policy's PolicyType custom field is "Automobile Trades" (ASR) — triggers the CGL box override below */
  isAutomobileTradesPolicy?: boolean;
  insuredId?: string;
  fileParentId?: string;
  /** Stored coverages from editCert — triggers XML-based prefill instead of get-coverages API */
  editCoverages?: StoredCoverage[];
  /** Policies from editCert — used to resolve carrier names → insurer letter slots */
  editPolicies?: StoredPolicy[];
  onLoadingChange?: (loading: boolean) => void;
  /** Policy numbers that landed in any coverage row after a prefill (CGL, Auto, Umbrella, WC, Other), so the
   *  parent can flag a selected policy that filled nothing. Called with [] whenever coverages are cleared. */
  onCoverageMapping?: (mappedPolicyNumbers: string[]) => void;
  initialCglAddlInsd?: boolean;
  initialCglSbrWvd?: boolean;
  initialAutoAddlInsd?: boolean;
  initialAutoSbrWvd?: boolean;
  initialUmbrellaAddlInsd?: boolean;
  initialUmbrellaSbrWvd?: boolean;
  initialWcAddlInsd?: boolean;
  initialWcSbrWvd?: boolean;
  initialOther1AddlInsd?: boolean;
  initialOther1SbrWvd?: boolean;
}

const Acord25Form = React.forwardRef<Acord25FormRef, Acord25FormProps>(
function Acord25Form({ showAcord101 = false, selectedPolicies = [], acord101AdditionalNamedInsureds = '', isAutomobileTradesPolicy = false, insuredId = '', fileParentId = 'E5832063-F5A6-40D2-9F85-450461D9523E', editCoverages, editPolicies, onLoadingChange, onCoverageMapping, initialCglAddlInsd = false, initialCglSbrWvd = false, initialAutoAddlInsd = false, initialAutoSbrWvd = false, initialUmbrellaAddlInsd = false, initialUmbrellaSbrWvd = false, initialWcAddlInsd = false, initialWcSbrWvd = false, initialOther1AddlInsd = false, initialOther1SbrWvd = false }, ref) {
  // Tracker #241: explain the blank cell while no policy is selected. Display only; never saved.
  const noPolicyPlaceholder = selectedPolicies.length === 0 ? 'Select a policy above or type a number' : undefined;
  const { client } = useSelectedClient();
  const [showCreateCarrier, setShowCreateCarrier] = React.useState(false);
  const [showAddSignature, setShowAddSignature] = React.useState(false);
  const [producerAddress, setProducerAddress] = React.useState('');
  const [producerCity, setProducerCity] = React.useState('');
  const [producerStreet, setProducerStreet] = React.useState('');
  const [producerState, setProducerState] = React.useState('');
  const [producerZip, setProducerZip] = React.useState('');
  const [contactName, setContactName] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [contactFax, setContactFax] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [insuredName, setInsuredName] = React.useState('');
  const [insuredAddress, setInsuredAddress] = React.useState('');
  const [insuredCity, setInsuredCity] = React.useState('');
  const [insuredStreet, setInsuredStreet] = React.useState('');
  const [insuredState, setInsuredState] = React.useState('');
  const [insuredZip, setInsuredZip] = React.useState('');
  const [insurerA, setInsurerA] = React.useState('');
  const [insurerB, setInsurerB] = React.useState('');
  const [insurerC, setInsurerC] = React.useState('');
  const [insurerD, setInsurerD] = React.useState('');
  const [insurerE, setInsurerE] = React.useState('');
  const [insurerF, setInsurerF] = React.useState('');
  const [insurerAId, setInsurerAId] = React.useState<string | null>(null);
  const [insurerBId, setInsurerBId] = React.useState<string | null>(null);
  const [insurerCId, setInsurerCId] = React.useState<string | null>(null);
  const [insurerDId, setInsurerDId] = React.useState<string | null>(null);
  const [insurerEId, setInsurerEId] = React.useState<string | null>(null);
  const [insurerFId, setInsurerFId] = React.useState<string | null>(null);
  // Tracker #257: agent-typed cells survive a policy change unless new policy data competes.
  const { own } = React.useRef(createMachineOwned()).current;
  const [insurerANaic, setInsurerANaic] = React.useState('');
  const [insurerBNaic, setInsurerBNaic] = React.useState('');
  const [insurerCNaic, setInsurerCNaic] = React.useState('');
  const [insurerDNaic, setInsurerDNaic] = React.useState('');
  const [insurerENaic, setInsurerENaic] = React.useState('');
  const [insurerFNaic, setInsurerFNaic] = React.useState('');

  const [cglClaimsMade, setCglClaimsMade] = React.useState(false);
  const [cglOccur, setCglOccur] = React.useState(false);
  const [cglPolicyNumber, setCglPolicyNumber] = React.useState('');
  const [cglEffDate, setCglEffDate] = React.useState<Date | null>(null);
  const [cglExpDate, setCglExpDate] = React.useState<Date | null>(null);
  const [cglEachOccurrence, setCglEachOccurrence] = React.useState('');
  const [cglDamageRented, setCglDamageRented] = React.useState('');
  const [cglMedExp, setCglMedExp] = React.useState('');
  const [cglPersonalAdv, setCglPersonalAdv] = React.useState('');
  const [cglGeneralAgg, setCglGeneralAgg] = React.useState('');
  const [cglProductsAgg, setCglProductsAgg] = React.useState('');

  const [cglPolicy, setCglPolicy] = React.useState(false);
  const [cglProject, setCglProject] = React.useState(false);
  const [cglLoc, setCglLoc] = React.useState(false);
  const [cglOther, setCglOther] = React.useState(false);
  const [cglOtherText, setCglOtherText] = React.useState('');
  const [cglAddlInsd, setCglAddlInsd] = React.useState(initialCglAddlInsd);
  const [cglSbrWvd, setCglSbrWvd] = React.useState(initialCglSbrWvd);
  React.useEffect(() => { setCglAddlInsd(initialCglAddlInsd); }, [initialCglAddlInsd]);
  React.useEffect(() => { setCglSbrWvd(initialCglSbrWvd); }, [initialCglSbrWvd]);

  const [autoAddlInsd, setAutoAddlInsd] = React.useState(initialAutoAddlInsd);
  const [autoSbrWvd, setAutoSbrWvd] = React.useState(initialAutoSbrWvd);
  React.useEffect(() => { setAutoAddlInsd(initialAutoAddlInsd); }, [initialAutoAddlInsd]);
  React.useEffect(() => { setAutoSbrWvd(initialAutoSbrWvd); }, [initialAutoSbrWvd]);

  const [umbrellaAddlInsd, setUmbrellaAddlInsd] = React.useState(initialUmbrellaAddlInsd);
  const [umbrellaSbrWvd, setUmbrellaSbrWvd] = React.useState(initialUmbrellaSbrWvd);
  React.useEffect(() => { setUmbrellaAddlInsd(initialUmbrellaAddlInsd); }, [initialUmbrellaAddlInsd]);
  React.useEffect(() => { setUmbrellaSbrWvd(initialUmbrellaSbrWvd); }, [initialUmbrellaSbrWvd]);

  const [wcAddlInsd, setWcAddlInsd] = React.useState(initialWcAddlInsd);
  const [wcSbrWvd, setWcSbrWvd] = React.useState(initialWcSbrWvd);
  React.useEffect(() => { setWcAddlInsd(initialWcAddlInsd); }, [initialWcAddlInsd]);
  React.useEffect(() => { setWcSbrWvd(initialWcSbrWvd); }, [initialWcSbrWvd]);

  const [other1AddlInsd, setOther1AddlInsd] = React.useState(initialOther1AddlInsd);
  const [other1SbrWvd, setOther1SbrWvd] = React.useState(initialOther1SbrWvd);
  React.useEffect(() => { setOther1AddlInsd(initialOther1AddlInsd); }, [initialOther1AddlInsd]);
  React.useEffect(() => { setOther1SbrWvd(initialOther1SbrWvd); }, [initialOther1SbrWvd]);

  const [autoAnyAuto, setAutoAnyAuto] = React.useState(false);
  const [autoAllOwned, setAutoAllOwned] = React.useState(false);
  const [autoScheduled, setAutoScheduled] = React.useState(false);
  const [autoHired, setAutoHired] = React.useState(false);
  const [autoNonOwned, setAutoNonOwned] = React.useState(false);
  const [autoPolicyNumber, setAutoPolicyNumber] = React.useState('');
  const [autoEffDate, setAutoEffDate] = React.useState<Date | null>(null);
  const [autoExpDate, setAutoExpDate] = React.useState<Date | null>(null);
  const [autoCombinedSingle, setAutoCombinedSingle] = React.useState('');
  const [autoBodilyPerson, setAutoBodilyPerson] = React.useState('');
  const [autoBodilyAccident, setAutoBodilyAccident] = React.useState('');
  const [autoPropertyDamage, setAutoPropertyDamage] = React.useState('');

  const [umbrellaLiab, setUmbrellaLiab] = React.useState(false);
  const [excessLiab, setExcessLiab] = React.useState(false);
  const [umbrellaOccur, setUmbrellaOccur] = React.useState(false);
  const [umbrellaClaimsMade, setUmbrellaClaimsMade] = React.useState(false);
  const [umbrellaRetention, setUmbrellaRetention] = React.useState('');
  const [umbrellaPolicyNumber, setUmbrellaPolicyNumber] = React.useState('');
  const [umbrellaEffDate, setUmbrellaEffDate] = React.useState<Date | null>(null);
  const [umbrellaExpDate, setUmbrellaExpDate] = React.useState<Date | null>(null);
  const [umbrellaEachOccurrence, setUmbrellaEachOccurrence] = React.useState('');
  const [umbrellaAggregate, setUmbrellaAggregate] = React.useState('');
  const [umbrellaDed, setUmbrellaDed] = React.useState(false);
  const [umbrellaRetentionCheck, setUmbrellaRetentionCheck] = React.useState(false);

  const [wcPolicyNumber, setWcPolicyNumber] = React.useState('');
  const [wcEffDate, setWcEffDate] = React.useState<Date | null>(null);
  const [wcExpDate, setWcExpDate] = React.useState<Date | null>(null);
  const [wcPerStatute, setWcPerStatute] = React.useState(false);
  const [wcOther, setWcOther] = React.useState(false);
  const [wcExcluded, setWcExcluded] = React.useState(false);
  const [wcEachAccident, setWcEachAccident] = React.useState('');
  const [wcDiseaseEmployee, setWcDiseaseEmployee] = React.useState('');
  const [wcDiseasePolicyLimit, setWcDiseasePolicyLimit] = React.useState('');
  const [wcSpecialProvisions, setWcSpecialProvisions] = React.useState('');

  const [cglBlank1Check, setCglBlank1Check] = React.useState(false);
  const [cglBlank1Text, setCglBlank1Text] = React.useState('');
  const [cglBlank2Check, setCglBlank2Check] = React.useState(false);
  const [cglBlank2Text, setCglBlank2Text] = React.useState('');
  const [cglBlankLimitLabel, setCglBlankLimitLabel] = React.useState('');
  const [cglBlankLimitAmt, setCglBlankLimitAmt] = React.useState('');

  const [autoBlank1Check, setAutoBlank1Check] = React.useState(false);
  const [autoBlank1Text, setAutoBlank1Text] = React.useState('');
  const [autoBlank2Check, setAutoBlank2Check] = React.useState(false);
  const [autoBlank2Text, setAutoBlank2Text] = React.useState('');
  const [autoBlankLimitLabel, setAutoBlankLimitLabel] = React.useState('');
  const [autoBlankLimitAmt, setAutoBlankLimitAmt] = React.useState('');

  const [umbrellaBlankLimitLabel, setUmbrellaBlankLimitLabel] = React.useState('');
  const [umbrellaBlankLimitAmt, setUmbrellaBlankLimitAmt] = React.useState('');

  const [wcOtherText, setWcOtherText] = React.useState('');

  const [descriptionOfOps, setDescriptionOfOps] = React.useState('');
  // const autoDescPrefilled = React.useRef(false);
  const [holderName, setHolderName] = React.useState('');
  const [holderAddress, setHolderAddress] = React.useState('');
  const [holderCity, setHolderCity] = React.useState('');
  const [holderStreet, setHolderStreet] = React.useState('');
  const [holderState, setHolderState] = React.useState('');
  const [holderZip, setHolderZip] = React.useState('');
  const [holderSearchQuery, setHolderSearchQuery] = React.useState('');
  const [holderSuggestions, setHolderSuggestions] = React.useState<{ label: string; value: any }[]>([]);

  const fetchHolderSuggestions = React.useCallback(async (query: string) => {
    if (query.length <= 1) return;
    try {
      const NCToken = localStorage.getItem('NCToken');
      const igtoken = localStorage.getItem('igtoken');
      const response = await fetch(
        `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-holders?top=10&search=${encodeURIComponent(query)}`,
        { headers: { 'NCToken': NCToken || '', 'Authorization': `Bearer ${igtoken || ''}` } }
      );
      if (!response.ok) return;
      const data = await response.json();
      const ncUserId = localStorage.getItem('NCUserId');
      const all: { label: string; value: any }[] = (data.value || []).map((item: any) => ({ label: item.name, value: item }));
      const filtered = all.filter((item) => {
        const aiDetails: any[] = (item.value?.additionalInsuredDetails ?? [])
          .filter((ai: any) => ai.truckingCompanyId === insuredId);
        if (aiDetails.length === 0) return item.value?.lastChangeUserId === ncUserId;
        if (selectedPolicies.length > 0) {
          return aiDetails.some((ai: any) => {
            const aiPolicyIds: string[] = (ai.policies ?? []).map((p: any) => p.databaseId as string);
            return aiPolicyIds.some(pid => selectedPolicies.includes(pid));
          });
        }
        return true;
      });
      setHolderSuggestions(filtered);
    } catch {
      // non-critical
    }
  }, [insuredId, selectedPolicies]);
  const [holderEnabled, setHolderEnabled] = React.useState(false);
  const [authRepSignature, setAuthRepSignature] = React.useState('');
  const { signatureOptions, refetchSignatures } = useSignatures();
  const [signaturePreviewUrl, setSignaturePreviewUrl] = React.useState('');
  React.useEffect(() => {
    if (!authRepSignature) { setSignaturePreviewUrl(''); return; }
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/signature-preview/${authRepSignature}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
        'Content-Type': 'application/json',
        NCToken: localStorage.getItem('NCToken') || '',
      },
    })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(url => setSignaturePreviewUrl(url.replace(/^"|"$/g, '')))
      .catch(() => setSignaturePreviewUrl(''));
  }, [authRepSignature]);
  const [certDate, setCertDate] = React.useState<Date | null>(new Date());
  const [certNumber, setCertNumber] = React.useState('');
  const [revisionNumber, setRevisionNumber] = React.useState('');

  // True on the first selectedPolicies change in edit mode — consume stored XML
  // instead of calling get-coverages. Reset to false afterward so subsequent
  // policy changes re-fetch from the API as normal.
  const editPrefillPending = React.useRef(!!(editCoverages && editCoverages.length > 0));
  // Stable flag set once at mount — never changes, unlike editPrefillPending above.
  const isEditMode = React.useRef(!!(editCoverages && editCoverages.length > 0));
  // A cert saved with no linked policies still has stored coverage rows (blank numbers and
  // limits), but the "restore on first policy change" trigger never fires for it. Track that
  // so the coverage effect can restore it immediately instead (see there).
  const editHasLinkedPolicies = React.useRef(!!(editPolicies && editPolicies.length > 0));

  // Auto-prefill Description of Operations when Automobile Liability custom amount is filled
  // React.useEffect(() => {
  //   if (!autoBlankLimitAmt) return;
  //   const generated = `IF APPLICABLE WILL BE DELIVERED WITH POLICY Garage Keepers Coverage: ${autoBlankLimitAmt}`;
  //   setDescriptionOfOps(prev => {
  //     // Replace previously auto-generated line if present, otherwise append
  //     if (autoDescPrefilled.current) {
  //       return prev.replace(/IF APPLICABLE WILL BE DELIVERED WITH POLICY Garage Keepers Coverage: [^\n]*/g, generated).trim();
  //     }
  //     autoDescPrefilled.current = true;
  //     return prev ? `${prev}\n${generated}` : generated;
  //   });
  // }, [autoBlankLimitAmt]);

  const [other1InsurerLtr, setOther1InsurerLtr] = React.useState('');
  const [other1Type, setOther1Type] = React.useState('');
  const [other1PolicyNumber, setOther1PolicyNumber] = React.useState('');
  const [other1EffDate, setOther1EffDate] = React.useState<Date | null>(null);
  const [other1ExpDate, setOther1ExpDate] = React.useState<Date | null>(null);
  const [other1LimitLabel1, setOther1LimitLabel1] = React.useState('');
  const [other1LimitAmt1, setOther1LimitAmt1] = React.useState('');
  const [other1LimitLabel2, setOther1LimitLabel2] = React.useState('');
  const [other1LimitAmt2, setOther1LimitAmt2] = React.useState('');
  const [other1LimitLabel3, setOther1LimitLabel3] = React.useState('');
  const [other1LimitAmt3, setOther1LimitAmt3] = React.useState('');
  // keep other2 state vars for payload backward compat but not shown in UI
  const other2InsurerLtr = '';
  const other2Type = '';
  const other2PolicyNumber = '';
  const other2EffDate: Date | null = null;
  const other2ExpDate: Date | null = null;
  const other2Limits = '';
  const [acord101Agency, setAcord101Agency] = React.useState('');
  const [acord101NamedInsured, setAcord101NamedInsured] = React.useState('');
  const [acord101AdditionalRemarks, setAcord101AdditionalRemarks] = React.useState('');

  const [cglInsurerLtr, setCglInsurerLtr] = React.useState('');
  const [autoInsurerLtr, setAutoInsurerLtr] = React.useState('');
  const [umbrellaInsurerLtr, setUmbrellaInsurerLtr] = React.useState('');
  const [wcInsurerLtr, setWcInsurerLtr] = React.useState('');

  const clearCoverages = () => {
    setInsurerA(own('InsurerA', '')); setInsurerB(own('InsurerB', '')); setInsurerC(own('InsurerC', ''));
    setInsurerD(own('InsurerD', '')); setInsurerE(own('InsurerE', '')); setInsurerF(own('InsurerF', ''));
    setInsurerANaic(own('InsurerANaic', '')); setInsurerBNaic(own('InsurerBNaic', '')); setInsurerCNaic(own('InsurerCNaic', ''));
    setInsurerDNaic(own('InsurerDNaic', '')); setInsurerENaic(own('InsurerENaic', '')); setInsurerFNaic(own('InsurerFNaic', ''));
    setInsurerAId(own('InsurerAId', null)); setInsurerBId(own('InsurerBId', null)); setInsurerCId(own('InsurerCId', null));
    setInsurerDId(own('InsurerDId', null)); setInsurerEId(own('InsurerEId', null)); setInsurerFId(own('InsurerFId', null));
    setCglInsurerLtr(own('CglInsurerLtr', '')); setCglPolicyNumber(own('CglPolicyNumber', '')); setCglEffDate(own('CglEffDate', null)); setCglExpDate(own('CglExpDate', null));
    setCglClaimsMade(own('CglClaimsMade', false)); setCglOccur(own('CglOccur', false));
    setCglPolicy(own('CglPolicy', false)); setCglProject(own('CglProject', false)); setCglLoc(own('CglLoc', false));
    setCglOther(own('CglOther', false)); setCglOtherText(own('CglOtherText', ''));
    setCglEachOccurrence(own('CglEachOccurrence', '')); setCglDamageRented(own('CglDamageRented', '')); setCglMedExp(own('CglMedExp', ''));
    setCglPersonalAdv(own('CglPersonalAdv', '')); setCglGeneralAgg(own('CglGeneralAgg', '')); setCglProductsAgg(own('CglProductsAgg', ''));
    setAutoInsurerLtr(own('AutoInsurerLtr', '')); setAutoPolicyNumber(own('AutoPolicyNumber', '')); setAutoEffDate(own('AutoEffDate', null)); setAutoExpDate(own('AutoExpDate', null));
    setAutoAnyAuto(own('AutoAnyAuto', false)); setAutoAllOwned(own('AutoAllOwned', false)); setAutoScheduled(own('AutoScheduled', false));
    setAutoHired(own('AutoHired', false)); setAutoNonOwned(own('AutoNonOwned', false));
    setAutoCombinedSingle(own('AutoCombinedSingle', '')); setAutoBodilyPerson(own('AutoBodilyPerson', ''));
    setAutoBodilyAccident(own('AutoBodilyAccident', '')); setAutoPropertyDamage(own('AutoPropertyDamage', ''));
    setUmbrellaInsurerLtr(own('UmbrellaInsurerLtr', '')); setUmbrellaPolicyNumber(own('UmbrellaPolicyNumber', '')); setUmbrellaEffDate(own('UmbrellaEffDate', null)); setUmbrellaExpDate(own('UmbrellaExpDate', null));
    setUmbrellaLiab(own('UmbrellaLiab', false)); setExcessLiab(own('ExcessLiab', false));
    setUmbrellaOccur(own('UmbrellaOccur', false)); setUmbrellaClaimsMade(own('UmbrellaClaimsMade', false));
    setUmbrellaDed(own('UmbrellaDed', false)); setUmbrellaRetentionCheck(own('UmbrellaRetentionCheck', false)); setUmbrellaRetention(own('UmbrellaRetention', ''));
    setUmbrellaEachOccurrence(own('UmbrellaEachOccurrence', '')); setUmbrellaAggregate(own('UmbrellaAggregate', ''));
    setWcInsurerLtr(own('WcInsurerLtr', '')); setWcPolicyNumber(own('WcPolicyNumber', '')); setWcEffDate(own('WcEffDate', null)); setWcExpDate(own('WcExpDate', null));
    setWcPerStatute(own('WcPerStatute', false)); setWcOther(own('WcOther', false)); setWcExcluded(own('WcExcluded', false));
    setWcEachAccident(own('WcEachAccident', '')); setWcDiseaseEmployee(own('WcDiseaseEmployee', ''));
    setWcDiseasePolicyLimit(own('WcDiseasePolicyLimit', '')); setWcSpecialProvisions(own('WcSpecialProvisions', ''));
    setCglBlank1Check(own('CglBlank1Check', false)); setCglBlank1Text(own('CglBlank1Text', ''));
    setCglBlank2Check(own('CglBlank2Check', false)); setCglBlank2Text(own('CglBlank2Text', ''));
    setCglBlankLimitLabel(own('CglBlankLimitLabel', '')); setCglBlankLimitAmt(own('CglBlankLimitAmt', ''));
    setAutoBlank1Check(own('AutoBlank1Check', false)); setAutoBlank1Text(own('AutoBlank1Text', ''));
    setAutoBlank2Check(own('AutoBlank2Check', false)); setAutoBlank2Text(own('AutoBlank2Text', ''));
    setAutoBlankLimitLabel(own('AutoBlankLimitLabel', '')); setAutoBlankLimitAmt(own('AutoBlankLimitAmt', ''));
    setUmbrellaBlankLimitLabel(own('UmbrellaBlankLimitLabel', '')); setUmbrellaBlankLimitAmt(own('UmbrellaBlankLimitAmt', ''));
    setWcOtherText(own('WcOtherText', ''));
    setOther1InsurerLtr(own('Other1InsurerLtr', '')); setOther1Type(own('Other1Type', '')); setOther1PolicyNumber(own('Other1PolicyNumber', ''));
    setOther1EffDate(own('Other1EffDate', null)); setOther1ExpDate(own('Other1ExpDate', null));
    setOther1LimitLabel1(own('Other1LimitLabel1', '')); setOther1LimitAmt1(own('Other1LimitAmt1', ''));
    setOther1LimitLabel2(own('Other1LimitLabel2', '')); setOther1LimitAmt2(own('Other1LimitAmt2', ''));
    setOther1LimitLabel3(own('Other1LimitLabel3', '')); setOther1LimitAmt3(own('Other1LimitAmt3', ''));
    setDescriptionOfOps(own('DescriptionOfOps', ''));
  };

  const prefillFromApi = (data: AcordFieldItem[]) => {
    const ins = getInsurers(data);
    setInsurerA(own('InsurerA', ins.A.name)); setInsurerB(own('InsurerB', ins.B.name)); setInsurerC(own('InsurerC', ins.C.name));
    setInsurerD(own('InsurerD', ins.D.name)); setInsurerE(own('InsurerE', ins.E.name)); setInsurerF(own('InsurerF', ins.F.name));
    setInsurerANaic(own('InsurerANaic', ins.A.naic)); setInsurerBNaic(own('InsurerBNaic', ins.B.naic)); setInsurerCNaic(own('InsurerCNaic', ins.C.naic));
    setInsurerDNaic(own('InsurerDNaic', ins.D.naic)); setInsurerENaic(own('InsurerENaic', ins.E.naic)); setInsurerFNaic(own('InsurerFNaic', ins.F.naic));

    // Resolve carrier IDs async so NaicId is populated when the form is saved
    const slotEntries: Array<[string, React.Dispatch<React.SetStateAction<string | null>>]> = [
      [ins.A.name, setInsurerAId], [ins.B.name, setInsurerBId],
      [ins.C.name, setInsurerCId], [ins.D.name, setInsurerDId],
      [ins.E.name, setInsurerEId], [ins.F.name, setInsurerFId],
    ];
    slotEntries.forEach(([name, setter], i) => {
      if (name) resolveCarrierIdByName(name).then(id => { if (id) setter(own(`Insurer${'ABCDEF'[i]}Id`, id)); });
    });

    const cgl = mapFields(data, CGL_MAP);
    setCglInsurerLtr(own('CglInsurerLtr', cgl.insurerLtr));
    setCglPolicyNumber(own('CglPolicyNumber', cgl.policyNumber));
    setCglEffDate(own('CglEffDate', cgl.effDate));
    setCglExpDate(own('CglExpDate', cgl.expDate));
    setCglClaimsMade(own('CglClaimsMade', cgl.claimsMade));
    setCglOccur(own('CglOccur', cgl.occur));
    setCglBlank1Check(own('CglBlank1Check', cgl.blank1Check));
    setCglBlank1Text(own('CglBlank1Text', cgl.blank1Text));
    setCglBlank2Check(own('CglBlank2Check', cgl.blank2Check));
    setCglBlank2Text(own('CglBlank2Text', cgl.blank2Text));
    setCglPolicy(own('CglPolicy', cgl.policy));
    setCglProject(own('CglProject', cgl.project));
    setCglLoc(own('CglLoc', cgl.loc));
    setCglOther(own('CglOther', cgl.otherAgg));
    setCglOtherText(own('CglOtherText', cgl.otherAggText));
    setCglEachOccurrence(own('CglEachOccurrence', cgl.eachOccurrence));
    setCglDamageRented(own('CglDamageRented', cgl.damageRented));
    setCglMedExp(own('CglMedExp', cgl.medExp));
    setCglPersonalAdv(own('CglPersonalAdv', cgl.personalAdv));
    setCglGeneralAgg(own('CglGeneralAgg', cgl.generalAgg));
    setCglProductsAgg(own('CglProductsAgg', cgl.productsAgg));
    setCglBlankLimitLabel(own('CglBlankLimitLabel', cgl.blankLimitLabel));
    setCglBlankLimitAmt(own('CglBlankLimitAmt', cgl.blankLimitAmt));

    const auto = mapFields(data, AUTO_MAP);
    setAutoInsurerLtr(own('AutoInsurerLtr', auto.insurerLtr));
    setAutoPolicyNumber(own('AutoPolicyNumber', auto.policyNumber));
    setAutoEffDate(own('AutoEffDate', auto.effDate));
    setAutoExpDate(own('AutoExpDate', auto.expDate));
    setAutoAnyAuto(own('AutoAnyAuto', auto.anyAuto));
    setAutoAllOwned(own('AutoAllOwned', auto.allOwned));
    setAutoScheduled(own('AutoScheduled', auto.scheduled));
    setAutoHired(own('AutoHired', auto.hired));
    setAutoNonOwned(own('AutoNonOwned', auto.nonOwned));
    setAutoBlank1Check(own('AutoBlank1Check', auto.blank1Check));
    setAutoBlank1Text(own('AutoBlank1Text', auto.blank1Text));
    setAutoBlank2Check(own('AutoBlank2Check', auto.blank2Check));
    setAutoBlank2Text(own('AutoBlank2Text', auto.blank2Text));
    setAutoCombinedSingle(own('AutoCombinedSingle', auto.combinedSingle));
    setAutoBodilyPerson(own('AutoBodilyPerson', auto.bodilyPerson));
    setAutoBodilyAccident(own('AutoBodilyAccident', auto.bodilyAccident));
    setAutoPropertyDamage(own('AutoPropertyDamage', auto.propertyDamage));
    setAutoBlankLimitLabel(own('AutoBlankLimitLabel', auto.blankLimitLabel));
    setAutoBlankLimitAmt(own('AutoBlankLimitAmt', auto.blankLimitAmt));

    const umb = mapFields(data, UMBRELLA_MAP);
    setUmbrellaInsurerLtr(own('UmbrellaInsurerLtr', umb.insurerLtr));
    setUmbrellaPolicyNumber(own('UmbrellaPolicyNumber', umb.policyNumber));
    setUmbrellaEffDate(own('UmbrellaEffDate', umb.effDate));
    setUmbrellaExpDate(own('UmbrellaExpDate', umb.expDate));
    setUmbrellaLiab(own('UmbrellaLiab', umb.umbrellaLiab));
    setExcessLiab(own('ExcessLiab', umb.excessLiab));
    setUmbrellaOccur(own('UmbrellaOccur', umb.occur));
    setUmbrellaClaimsMade(own('UmbrellaClaimsMade', umb.claimsMade));
    setUmbrellaDed(own('UmbrellaDed', umb.ded));
    setUmbrellaRetentionCheck(own('UmbrellaRetentionCheck', umb.retentionCheck));
    setUmbrellaRetention(own('UmbrellaRetention', umb.retention));
    setUmbrellaEachOccurrence(own('UmbrellaEachOccurrence', umb.eachOccurrence));
    setUmbrellaAggregate(own('UmbrellaAggregate', umb.aggregate));
    setUmbrellaBlankLimitLabel(own('UmbrellaBlankLimitLabel', umb.blankLimitLabel));
    setUmbrellaBlankLimitAmt(own('UmbrellaBlankLimitAmt', umb.blankLimitAmt));

    const wc = mapFields(data, WC_MAP);
    setWcInsurerLtr(own('WcInsurerLtr', wc.insurerLtr));
    setWcPolicyNumber(own('WcPolicyNumber', wc.policyNumber));
    setWcEffDate(own('WcEffDate', wc.effDate));
    setWcExpDate(own('WcExpDate', wc.expDate));
    setWcPerStatute(own('WcPerStatute', wc.perStatute));
    setWcOther(own('WcOther', wc.other));
    setWcOtherText(own('WcOtherText', wc.otherText));
    setWcSpecialProvisions(own('WcSpecialProvisions', wc.specialProvisions));
    setWcEachAccident(own('WcEachAccident', wc.eachAccident));
    setWcDiseaseEmployee(own('WcDiseaseEmployee', wc.diseaseEmployee));
    setWcDiseasePolicyLimit(own('WcDiseasePolicyLimit', wc.diseasePolicyLimit));
    if (data.some(f => f.FieldName === 'Policy_WorkersCompensation_SubrogationWaivedCode' && f.Value === '1')) {
      setWcSbrWvd(own('WcSbrWvd', true));
    }

    const o1 = mapFieldsAtIndex(data, OTHER_MAP, 0);
    setOther1InsurerLtr(own('Other1InsurerLtr', o1.insurerLtr));
    setOther1Type(own('Other1Type', o1.type));
    setOther1PolicyNumber(own('Other1PolicyNumber', o1.policyNumber));
    setOther1EffDate(own('Other1EffDate', o1.effDate));
    setOther1ExpDate(own('Other1ExpDate', o1.expDate));
    setOther1LimitLabel1(own('Other1LimitLabel1', o1.limitCode || ''));
    setOther1LimitAmt1(own('Other1LimitAmt1', o1.limits || ''));
    setOther1LimitLabel2(own('Other1LimitLabel2', o1.deductibleCode || ''));
    setOther1LimitAmt2(own('Other1LimitAmt2', o1.deductibleAmt || ''));
    setOther1LimitLabel3(own('Other1LimitLabel3', o1.limitCode3 || ''));
    setOther1LimitAmt3(own('Other1LimitAmt3', o1.limitAmt3 || ''));

    setDescriptionOfOps(own('DescriptionOfOps', getAllRemarkLines(data, 'CertificateOfLiabilityInsurance_ACORDForm_RemarkText_A')));
  };

  // Tracker #257: the POLICY NUMBER cells are editable, so report the row numbers whenever they
  // change (prefill, edit restore, clear, or a typed number). The parent's "didn't populate"
  // warning follows them, so typing an unmapped policy's number into a row clears it.
  // onCoverageMapping is a parent setState, deliberately not a dep.
  React.useEffect(() => {
    onCoverageMapping?.([cglPolicyNumber, autoPolicyNumber, umbrellaPolicyNumber, wcPolicyNumber, other1PolicyNumber]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cglPolicyNumber, autoPolicyNumber, umbrellaPolicyNumber, wcPolicyNumber, other1PolicyNumber]);

  // Insured block: filled from the client only while a policy is selected (Farmers, 9/24) — a
  // certificate with no policy is created blank, not pre-filled. A selected policy's address
  // then overrides these through get-coverages; any policy counts, even one that fills no
  // coverage row. Edit mode: insured comes from stored XML, not the live client.
  React.useEffect(() => {
    if (!client) return;
    if (isEditMode.current) { setAcord101NamedInsured(client.name || ''); return; }
    if (!selectedPolicies || selectedPolicies.length === 0) {
      setAcord101NamedInsured('');
      setInsuredName('');
      setInsuredAddress('');
      setInsuredStreet('');
      setInsuredCity('');
      setInsuredState('');
      setInsuredZip('');
      return;
    }
    setAcord101NamedInsured(client.name || '');
    setInsuredName(client.name || '');
    setInsuredAddress(client.addressLine1 || '');
    setInsuredStreet(client.addressLine2 || '');
    setInsuredCity(client.city || '');
    setInsuredState(client.state || '');
    setInsuredZip(client.zip || '');
  }, [client, selectedPolicies]);

  const handleProducerSelect = React.useCallback((p: ProducerLocation) => {
    const parts = [p.businessName, p.adressLine1, p.city, p.state, p.zip].filter(Boolean);
    setAcord101Agency(parts.join(', '));
  }, []);

  // Prefill the producer/contact block from the first selected policy's agent. With no policy
  // the block is blank (Farmers, 9/22): clear it on deselect so a previous pick doesn't linger.
  // Edit mode keeps whatever the stored XML restored.
  React.useEffect(() => {
    if (!selectedPolicies || selectedPolicies.length === 0) {
      if (isEditMode.current) return;
      setContactName(''); setContactPhone(''); setContactFax(''); setContactEmail('');
      setProducerAddress(''); setProducerStreet(''); setProducerCity(''); setProducerState(''); setProducerZip('');
      setAcord101Agency('');
      return;
    }
    fetchFirstPolicyAgentContact(selectedPolicies).then(async ({ agentId, name, phone, fax, email }) => {
      if (name) setContactName(name);
      if (phone) setContactPhone(phone);
      if (fax) setContactFax(fax);
      if (email) setContactEmail(email);
      if (agentId && name) {
        const { npnNumber, addressLine1, city, state, zipCode } = await fetchAgentContact(agentId);
        setProducerAddress(npnNumber ? `${name} (${npnNumber})` : name);
        if (addressLine1) setProducerStreet(addressLine1);
        if (city) setProducerCity(city);
        if (state) setProducerState(state);
        if (zipCode) setProducerZip(zipCode);
        const agencyParts = [name, addressLine1, city, state, zipCode].filter(Boolean);
        setAcord101Agency(agencyParts.join(', '));
      }
    });
  }, [selectedPolicies]);


  // Refresh the remarks textarea from selected policies' AdditionalNames custom field on
  // every policy selection change — re-derived fully each time, not merged with prior content.
  // Disabled for now — prod team isn't ready for the auto-filled remark yet.
  React.useEffect(() => {
    if (isEditMode.current) return;
    // setAcord101AdditionalRemarks(acord101AdditionalNamedInsureds ? `Additional Named Insured(s): ${acord101AdditionalNamedInsureds}` : '');
  }, [acord101AdditionalNamedInsureds]);

  // Shared by the ASR suppression effect below and the coverage-fetch effect — the fetch needs
  // to apply this synchronously with the state value it just resolved (not wait on insuredState
  // to change), since get-coverages can repopulate the full CGL box *after* insuredState settles
  // on the same value it already had, which means the effect below won't re-fire to re-suppress it.
  const applyAsrCglOverride = React.useCallback((stateValue: string) => {
    const normalizedState = stateValue.trim().toUpperCase();
    const isCaOrTx = ['CA', 'CALIFORNIA', 'TX', 'TEXAS'].includes(normalizedState);

    setCglInsurerLtr('');
    setCglPolicyNumber('');
    setCglEffDate(null);
    setCglExpDate(null);
    setCglClaimsMade(false);
    setCglOccur(false);
    setCglEachOccurrence('');
    setCglPersonalAdv('');
    setCglGeneralAgg('');
    setCglProductsAgg('');
    setCglBlank1Check(false); setCglBlank1Text('');
    setCglBlank2Check(false); setCglBlank2Text('');
    setCglOther(false); setCglOtherText('');
    setCglProject(false);
    setCglLoc(false);
    setCglPolicy(isCaOrTx);
    if (!isCaOrTx) {
      setCglDamageRented('');
      setCglMedExp('');
    }
  }, []);

  React.useEffect(() => {
    clearCoverages();
    const noPolicySelected = !selectedPolicies || selectedPolicies.length === 0;

    // Edit mode: on the first policy change, restore the stored coverage XML instead of
    // calling get-coverages (which returns policy defaults, not saved values). A cert saved
    // with no linked policies never gets that first change, so restore it right away; if we
    // waited, the pending flag would survive and the agent's first policy pick would restore
    // the blank stored XML instead of fetching live coverages (Farmers report 9/21, policy
    // 605490300, tracker #241).
    if (editPrefillPending.current && (!noPolicySelected || !editHasLinkedPolicies.current)) {
      editPrefillPending.current = false;
      if (editCoverages && editCoverages.length > 0) {
        const prefill = parseAcord25StoredCoverages(editCoverages, editPolicies || []);
        if (prefill.insuredName)    setInsuredName(own('InsuredName', prefill.insuredName));
        if (prefill.insuredAddress) setInsuredAddress(own('InsuredAddress', prefill.insuredAddress));
        if (prefill.insuredStreet)  setInsuredStreet(own('InsuredStreet', prefill.insuredStreet));
        if (prefill.insuredCity)    setInsuredCity(own('InsuredCity', prefill.insuredCity));
        if (prefill.insuredState)   setInsuredState(own('InsuredState', prefill.insuredState));
        if (prefill.insuredZip)     setInsuredZip(own('InsuredZip', prefill.insuredZip));
        setInsurerA(own('InsurerA', prefill.insurerA)); setInsurerB(own('InsurerB', prefill.insurerB)); setInsurerC(own('InsurerC', prefill.insurerC));
        setInsurerD(own('InsurerD', prefill.insurerD)); setInsurerE(own('InsurerE', prefill.insurerE)); setInsurerF(own('InsurerF', prefill.insurerF));
        setInsurerAId(own('InsurerAId', prefill.insurerAId)); setInsurerBId(own('InsurerBId', prefill.insurerBId)); setInsurerCId(own('InsurerCId', prefill.insurerCId));
        setInsurerDId(own('InsurerDId', prefill.insurerDId)); setInsurerEId(own('InsurerEId', prefill.insurerEId)); setInsurerFId(own('InsurerFId', prefill.insurerFId));
        const idsFromPrefill = [
          prefill.insurerAId, prefill.insurerBId, prefill.insurerCId,
          prefill.insurerDId, prefill.insurerEId, prefill.insurerFId,
        ];
        const namesFromPrefill = [
          prefill.insurerA, prefill.insurerB, prefill.insurerC,
          prefill.insurerD, prefill.insurerE, prefill.insurerF,
        ];
        const nameSetters = [setInsurerA, setInsurerB, setInsurerC, setInsurerD, setInsurerE, setInsurerF];
        const idSetters = [setInsurerAId, setInsurerBId, setInsurerCId, setInsurerDId, setInsurerEId, setInsurerFId];
        const naicSetters = [setInsurerANaic, setInsurerBNaic, setInsurerCNaic, setInsurerDNaic, setInsurerENaic, setInsurerFNaic];
        // The NAIC # is not stored on the certificate; it comes from the carrier record.
        const fillNaicFromId = (id: string, i: number) => {
          resolveCarrierById(id).then(o => { if (o?.naic) naicSetters[i](own(`Insurer${'ABCDEF'[i]}Naic`, o.naic)); });
        };
        idsFromPrefill.forEach((naiCid, i) => {
          if (naiCid) {
            // naiCid is the certificate's actual saved insurer selection — resolve its
            // name and let it win over the linked policy's carrierName fallback above.
            resolveCarrierById(naiCid).then(o => { if (o?.name) nameSetters[i](own(`Insurer${'ABCDEF'[i]}`, o.name)); if (o?.naic) naicSetters[i](own(`Insurer${'ABCDEF'[i]}Naic`, o.naic)); });
          } else if (namesFromPrefill[i]) {
            // Legacy certs with no stored naiCid — resolve an id from the fallback name.
            resolveCarrierIdByName(namesFromPrefill[i]).then(id => { if (id) { idSetters[i](own(`Insurer${'ABCDEF'[i]}Id`, id)); fillNaicFromId(id, i); } });
          }
        });
        setCglInsurerLtr(own('CglInsurerLtr', prefill.cglInsurerLtr));
        setCglPolicyNumber(own('CglPolicyNumber', prefill.cglPolicyNumber));
        setCglEffDate(own('CglEffDate', prefill.cglEffDate)); setCglExpDate(own('CglExpDate', prefill.cglExpDate));
        setCglClaimsMade(own('CglClaimsMade', prefill.cglClaimsMade)); setCglOccur(own('CglOccur', prefill.cglOccur));
        setCglPolicy(own('CglPolicy', prefill.cglPolicy)); setCglProject(own('CglProject', prefill.cglProject));
        setCglLoc(own('CglLoc', prefill.cglLoc)); setCglOther(own('CglOther', prefill.cglOther)); setCglOtherText(own('CglOtherText', prefill.cglOtherText));
        setCglBlank1Check(own('CglBlank1Check', prefill.cglBlank1Check)); setCglBlank1Text(own('CglBlank1Text', prefill.cglBlank1Text));
        setCglBlank2Check(own('CglBlank2Check', prefill.cglBlank2Check)); setCglBlank2Text(own('CglBlank2Text', prefill.cglBlank2Text));
        setCglEachOccurrence(own('CglEachOccurrence', prefill.cglEachOccurrence)); setCglDamageRented(own('CglDamageRented', prefill.cglDamageRented));
        setCglMedExp(own('CglMedExp', prefill.cglMedExp)); setCglPersonalAdv(own('CglPersonalAdv', prefill.cglPersonalAdv));
        setCglGeneralAgg(own('CglGeneralAgg', prefill.cglGeneralAgg)); setCglProductsAgg(own('CglProductsAgg', prefill.cglProductsAgg));
        setCglBlankLimitLabel(own('CglBlankLimitLabel', prefill.cglBlankLimitLabel)); setCglBlankLimitAmt(own('CglBlankLimitAmt', prefill.cglBlankLimitAmt));
        setAutoInsurerLtr(own('AutoInsurerLtr', prefill.autoInsurerLtr));
        setAutoPolicyNumber(own('AutoPolicyNumber', prefill.autoPolicyNumber));
        setAutoEffDate(own('AutoEffDate', prefill.autoEffDate)); setAutoExpDate(own('AutoExpDate', prefill.autoExpDate));
        setAutoAnyAuto(own('AutoAnyAuto', prefill.autoAnyAuto)); setAutoAllOwned(own('AutoAllOwned', prefill.autoAllOwned));
        setAutoScheduled(own('AutoScheduled', prefill.autoScheduled)); setAutoHired(own('AutoHired', prefill.autoHired));
        setAutoNonOwned(own('AutoNonOwned', prefill.autoNonOwned));
        setAutoBlank1Check(own('AutoBlank1Check', prefill.autoBlank1Check)); setAutoBlank1Text(own('AutoBlank1Text', prefill.autoBlank1Text));
        setAutoBlank2Check(own('AutoBlank2Check', prefill.autoBlank2Check)); setAutoBlank2Text(own('AutoBlank2Text', prefill.autoBlank2Text));
        setAutoCombinedSingle(own('AutoCombinedSingle', prefill.autoCombinedSingle));
        setAutoBodilyPerson(own('AutoBodilyPerson', prefill.autoBodilyPerson));
        setAutoBodilyAccident(own('AutoBodilyAccident', prefill.autoBodilyAccident));
        setAutoPropertyDamage(own('AutoPropertyDamage', prefill.autoPropertyDamage));
        setAutoBlankLimitLabel(own('AutoBlankLimitLabel', prefill.autoBlankLimitLabel)); setAutoBlankLimitAmt(own('AutoBlankLimitAmt', prefill.autoBlankLimitAmt));
        setUmbrellaInsurerLtr(own('UmbrellaInsurerLtr', prefill.umbrellaInsurerLtr));
        setUmbrellaPolicyNumber(own('UmbrellaPolicyNumber', prefill.umbrellaPolicyNumber));
        setUmbrellaEffDate(own('UmbrellaEffDate', prefill.umbrellaEffDate)); setUmbrellaExpDate(own('UmbrellaExpDate', prefill.umbrellaExpDate));
        setUmbrellaLiab(own('UmbrellaLiab', prefill.umbrellaLiab)); setExcessLiab(own('ExcessLiab', prefill.excessLiab));
        setUmbrellaOccur(own('UmbrellaOccur', prefill.umbrellaOccur)); setUmbrellaClaimsMade(own('UmbrellaClaimsMade', prefill.umbrellaClaimsMade));
        setUmbrellaDed(own('UmbrellaDed', prefill.umbrellaDed)); setUmbrellaRetentionCheck(own('UmbrellaRetentionCheck', prefill.umbrellaRetentionCheck));
        setUmbrellaRetention(own('UmbrellaRetention', prefill.umbrellaRetention));
        setUmbrellaEachOccurrence(own('UmbrellaEachOccurrence', prefill.umbrellaEachOccurrence));
        setUmbrellaAggregate(own('UmbrellaAggregate', prefill.umbrellaAggregate));
        setUmbrellaBlankLimitLabel(own('UmbrellaBlankLimitLabel', prefill.umbrellaBlankLimitLabel)); setUmbrellaBlankLimitAmt(own('UmbrellaBlankLimitAmt', prefill.umbrellaBlankLimitAmt));
        setWcInsurerLtr(own('WcInsurerLtr', prefill.wcInsurerLtr));
        setWcPolicyNumber(own('WcPolicyNumber', prefill.wcPolicyNumber));
        setWcEffDate(own('WcEffDate', prefill.wcEffDate)); setWcExpDate(own('WcExpDate', prefill.wcExpDate));
        setWcPerStatute(own('WcPerStatute', prefill.wcPerStatute)); setWcOther(own('WcOther', prefill.wcOther));
        setWcOtherText(own('WcOtherText', prefill.wcOtherText)); setWcExcluded(own('WcExcluded', prefill.wcExcluded));
        setWcEachAccident(own('WcEachAccident', prefill.wcEachAccident));
        setWcDiseaseEmployee(own('WcDiseaseEmployee', prefill.wcDiseaseEmployee));
        setWcDiseasePolicyLimit(own('WcDiseasePolicyLimit', prefill.wcDiseasePolicyLimit));
        setWcSpecialProvisions(own('WcSpecialProvisions', prefill.wcSpecialProvisions));
        setOther1InsurerLtr(own('Other1InsurerLtr', prefill.other1InsurerLtr));
        setOther1Type(own('Other1Type', prefill.other1Type));
        setOther1PolicyNumber(own('Other1PolicyNumber', prefill.other1PolicyNumber));
        setOther1EffDate(own('Other1EffDate', prefill.other1EffDate)); setOther1ExpDate(own('Other1ExpDate', prefill.other1ExpDate));
        setOther1LimitLabel1(own('Other1LimitLabel1', prefill.other1LimitLabel1)); setOther1LimitAmt1(own('Other1LimitAmt1', prefill.other1LimitAmt1));
        setOther1LimitLabel2(own('Other1LimitLabel2', prefill.other1LimitLabel2)); setOther1LimitAmt2(own('Other1LimitAmt2', prefill.other1LimitAmt2));
        setOther1LimitLabel3(own('Other1LimitLabel3', prefill.other1LimitLabel3)); setOther1LimitAmt3(own('Other1LimitAmt3', prefill.other1LimitAmt3));
        if (prefill.certNumber)     setCertNumber(own('CertNumber', prefill.certNumber));
        if (prefill.revisionNumber) setRevisionNumber(own('RevisionNumber', prefill.revisionNumber));
        setDescriptionOfOps(own('DescriptionOfOps', prefill.descriptionOfOps));
        if (prefill.certDate) setCertDate(own('CertDate', prefill.certDate));
        if (prefill.acord101AdditionalRemarks) setAcord101AdditionalRemarks(own('Acord101AdditionalRemarks', prefill.acord101AdditionalRemarks));
        if (prefill.authRepSignature) setAuthRepSignature(own('AuthRepSignature', prefill.authRepSignature));
        if (prefill.holderName) {
          setHolderEnabled(own('HolderEnabled', true));
          setHolderName(own('HolderName', prefill.holderName));
          setHolderAddress(own('HolderAddress', prefill.holderAddress));
          setHolderCity(own('HolderCity', prefill.holderCity));
          setHolderStreet(own('HolderStreet', prefill.holderStreet));
          setHolderState(own('HolderState', prefill.holderState));
          setHolderZip(own('HolderZip', prefill.holderZip));
        }
      }
      onLoadingChange?.(false);
      return;
    }

    if (noPolicySelected) return;

    let cancelled = false;
    const fetchCoverages = async () => {
      onLoadingChange?.(true);
      try {
        const ncToken = localStorage.getItem('NCToken') || '';
        const igToken = localStorage.getItem('igtoken') || '';
        const res = await fetch(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/get-coverages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'NCToken': ncToken,
              Authorization: `Bearer ${igToken}`,
            },
            body: JSON.stringify({
              policiesIds: selectedPolicies,
              truckingCompanyId: insuredId || '',
              fileParentId,
              naics: [{ Code: '', Name: '' }],
              Page: 'certificateFiller',
            }),
          }
        );
        if (cancelled) return;
        const json = await res.json();
        const arr: AcordFieldItem[] = Array.isArray(json.Data) ? json.Data : [];
        if (arr.length > 0) prefillFromApi(arr);

        // Runs whenever the policy selection changes (this branch is never reached on the
        // very first edit-mode load — that's handled by the XML-restore branch above).
        // Prefer the current first-selected policy's address; otherwise fall back to client.
        const policyAddress = pickPolicyAddress(getPolicyAddresses(arr), selectedPolicies[0]);
        if (policyAddress) {
          setInsuredAddress(policyAddress.AddressLine1 || '');
          setInsuredStreet(policyAddress.AddressLine2 || '');
          setInsuredCity(policyAddress.City || '');
          const resolvedState = policyAddress.State || '';
          setInsuredState(resolvedState);
          setInsuredZip(policyAddress.ZipCode || '');
          if (isAutomobileTradesPolicy) applyAsrCglOverride(resolvedState);
        } else if (client) {
          setInsuredAddress(client.addressLine1 || '');
          setInsuredStreet(client.addressLine2 || '');
          setInsuredCity(client.city || '');
          const resolvedState = client.state || '';
          setInsuredState(resolvedState);
          setInsuredZip(client.zip || '');
          if (isAutomobileTradesPolicy) applyAsrCglOverride(resolvedState);
        }
      } catch {
      } finally {
        if (!cancelled) onLoadingChange?.(false);
      }
    };
    fetchCoverages();
    // Also clear the parent's Loading state: if the agent switches forms while get-coverages is in
    // flight, the finally() above is skipped (cancelled) and nothing else would ever reset it.
    return () => { cancelled = true; onLoadingChange?.(false); };
  }, [selectedPolicies, insuredId]);

  // Automobile Trades (ASR) policies carry garage liability inside the auto policy itself, so the
  // standalone CGL box from get-coverages/stored XML needs to be suppressed or trimmed down after
  // it's populated above. All states except CA/TX exclude CGL entirely (it's covered under the
  // garage keepers form) — the CGL box shows nothing. CA and TX still require the CGL box, but only
  // the aggregate basis ("policy") plus the two garage-liability sublimits (damage to rented
  // premises, med exp) — no policy number, dates, or each-occurrence limit.
  // insuredState is free text (not a dropdown) sourced from NowCerts policy/client address data,
  // so it can come through as either the 2-letter code or the full state name — match both.
  // Covers manual edits to the state field after load; the initial policy-selection load is
  // handled synchronously inside the coverage-fetch effect above (see applyAsrCglOverride).
  React.useEffect(() => {
    if (!isAutomobileTradesPolicy) return;
    applyAsrCglOverride(insuredState);
  }, [isAutomobileTradesPolicy, insuredState, selectedPolicies, applyAsrCglOverride]);

  React.useImperativeHandle(ref, () => ({
    getFormData: (): Acord25FormData => {
      const resolvedAId = insurerAId ?? lookupCarrierIdSync(insurerA);
      const resolvedBId = insurerBId ?? lookupCarrierIdSync(insurerB);
      const resolvedCId = insurerCId ?? lookupCarrierIdSync(insurerC);
      const resolvedDId = insurerDId ?? lookupCarrierIdSync(insurerD);
      const resolvedEId = insurerEId ?? lookupCarrierIdSync(insurerE);
      const resolvedFId = insurerFId ?? lookupCarrierIdSync(insurerF);
      return {
      producerAddress, producerCity, producerStreet, producerState, producerZip,
      contactName, contactPhone, contactFax, contactEmail,
      insuredName, insuredAddress, insuredStreet, insuredCity, insuredState, insuredZip,
      insurerA, insurerB, insurerC, insurerD, insurerE, insurerF,
      insurerAId: resolvedAId, insurerBId: resolvedBId, insurerCId: resolvedCId,
      insurerDId: resolvedDId, insurerEId: resolvedEId, insurerFId: resolvedFId,
      insurerANaic, insurerBNaic, insurerCNaic, insurerDNaic, insurerENaic, insurerFNaic,
      cglInsurerLtr, cglPolicyNumber, cglEffDate, cglExpDate,
      cglClaimsMade, cglOccur, cglPolicy, cglProject, cglLoc, cglOther, cglOtherText,
      cglBlank1Check, cglBlank1Text, cglBlank2Check, cglBlank2Text,
      cglEachOccurrence, cglDamageRented, cglMedExp, cglPersonalAdv, cglGeneralAgg, cglProductsAgg,
      cglBlankLimitLabel, cglBlankLimitAmt,
      cglAddlInsd, cglSbrWvd,
      autoInsurerLtr, autoPolicyNumber, autoEffDate, autoExpDate,
      autoAnyAuto, autoAllOwned, autoScheduled, autoHired, autoNonOwned,
      autoBlank1Check, autoBlank1Text, autoBlank2Check, autoBlank2Text,
      autoCombinedSingle, autoBodilyPerson, autoBodilyAccident, autoPropertyDamage,
      autoBlankLimitLabel, autoBlankLimitAmt,
      autoAddlInsd, autoSbrWvd,
      umbrellaInsurerLtr, umbrellaPolicyNumber, umbrellaEffDate, umbrellaExpDate,
      umbrellaLiab, excessLiab, umbrellaOccur, umbrellaClaimsMade, umbrellaDed, umbrellaRetentionCheck, umbrellaRetention,
      umbrellaEachOccurrence, umbrellaAggregate,
      umbrellaBlankLimitLabel, umbrellaBlankLimitAmt,
      umbrellaAddlInsd, umbrellaSbrWvd,
      wcInsurerLtr, wcPolicyNumber, wcEffDate, wcExpDate,
      wcPerStatute, wcOther, wcOtherText, wcExcluded, wcEachAccident, wcDiseaseEmployee, wcDiseasePolicyLimit, wcSpecialProvisions,
      wcAddlInsd, wcSbrWvd,
      other1InsurerLtr, other1Type, other1PolicyNumber, other1EffDate, other1ExpDate,
      other1LimitLabel1, other1LimitAmt1, other1LimitLabel2, other1LimitAmt2, other1LimitLabel3, other1LimitAmt3,
      other1AddlInsd, other1SbrWvd,
      other2InsurerLtr, other2Type, other2PolicyNumber, other2EffDate, other2ExpDate, other2Limits,
      certNumber, revisionNumber, descriptionOfOps,
      holderName, holderAddress, holderCity, holderStreet, holderState, holderZip,
      authRepSignature,
      certDate,
      showAcord101,
      acord101Agency,
      acord101NamedInsured,
      acord101AdditionalRemarks,
      };
    },
  }));

  return (
    <>
      <div className="masterCert__section">
        <div className="masterCert__acordHeader">
          <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
          <div className="masterCert__acordTitleWrapper">
            <div className="masterCert__acordTitle">CERTIFICATE OF LIABILITY INSURANCE</div>
          </div>
          <div className="masterCert__dateWrapper">
            <div className="masterCert__dateLabel">DATE (MM/DD/YYYY)</div>
            <div className="masterCert__dateCell">
              <Calendar value={certDate} onChange={(e) => setCertDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
          </div>
        </div>

        <div className="masterCert__disclaimer">
          This certificate is issued as a matter of information only and confers no rights upon the certificate holder. This certificate does not affirmatively or negatively amend, extend or alter the coverage afforded by the policies below. This certificate of insurance does not constitute a contract between the issuing insurer(s), authorized representative or producer, and the certificate holder.
        </div>
        <div className="masterCert__disclaimerImportant">
          <span className="masterCert__importantLabel">IMPORTANT:</span> If the certificate holder is an Additional Insured, the policy(ies) must be endorsed. If SUBROGATION IS WAIVED, subject to the terms and conditions of the policy, certain policies may require an endorsement. A statement on this certificate does not confer rights to the certificate holder in lieu of such endorsement(s).
        </div>

        <div className="masterCert__twoColumnLayout">
          <div>
            <ProducerSection
              address={producerAddress}
              onAddressChange={setProducerAddress}
              city={producerCity}
              onCityChange={setProducerCity}
              street={producerStreet}
              onStreetChange={setProducerStreet}
              state={producerState}
              onStateChange={setProducerState}
              zip={producerZip}
              onZipChange={setProducerZip}
              onProducerSelect={handleProducerSelect}
              onAgentContact={(phone, fax, email) => { setContactPhone(phone); setContactFax(fax); setContactEmail(email); }}
            />

            <div>
              <div className="masterCert__sectionHeader">INSURED</div>
              <InputText maxLength={100} value={insuredName} onChange={(e) => setInsuredName(e.target.value)} placeholder="Type insured name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <InputText maxLength={100} value={insuredAddress} onChange={(e) => setInsuredAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <InputText maxLength={100} value={insuredStreet} onChange={(e) => setInsuredStreet(e.target.value)} placeholder="Address Line 1" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
              <div className="masterCert__addressGrid">
                <InputText maxLength={100} value={insuredCity} onChange={(e) => setInsuredCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={50} value={insuredState} onChange={(e) => setInsuredState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
                <InputText maxLength={20} value={insuredZip} onChange={(e) => setInsuredZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
          </div>

          <div>
            <div style={{ marginBottom: 16 }}>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">CONTACT NAME:</div>
                  <InputText maxLength={100} value={contactName} onChange={(e) => setContactName(e.target.value)} className="masterCert__input masterCert__inputFull" />
                </div>
                <div></div>
              </div>
              <div className="masterCert__contactGrid">
                <div>
                  <div className="masterCert__contactLabel">PHONE (A/C, No, Ext):</div>
                  <InputText value={formatUsPhone(contactPhone)} onChange={(e) => setContactPhone(e.target.value)} className="masterCert__input masterCert__inputFull" />
                </div>
                <div>
                  <div className="masterCert__contactLabel">FAX (A/C, No):</div>
                  <InputText value={formatUsPhone(contactFax)} onChange={(e) => setContactFax(e.target.value)} className="masterCert__input masterCert__inputFull" />
                </div>
              </div>
              <div>
                <div className="masterCert__contactLabel">E-MAIL ADDRESS:</div>
                <InputText maxLength={100} value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>INSURERS AFFORDING COVERAGE (and NAIC #)</div>
                <Button className="carrierDialog__secondaryBtn" label="Add New" icon="pi pi-plus" outlined size="small" style={{ flexShrink: 0, padding: '2px 10px', fontSize: '0.7rem', height: '28px' }} onClick={() => setShowCreateCarrier(true)} />
              </div>
              <div className="masterCert__insurersGrid">
                <div className="masterCert__insurerLabel">INSURER A:</div>
                <InsurerNaicField name={insurerA} id={insurerAId} naic={insurerANaic} onNameChange={setInsurerA} onIdChange={setInsurerAId} onNaicChange={setInsurerANaic} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER B:</div>
                <InsurerNaicField name={insurerB} id={insurerBId} naic={insurerBNaic} onNameChange={setInsurerB} onIdChange={setInsurerBId} onNaicChange={setInsurerBNaic} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER C:</div>
                <InsurerNaicField name={insurerC} id={insurerCId} naic={insurerCNaic} onNameChange={setInsurerC} onIdChange={setInsurerCId} onNaicChange={setInsurerCNaic} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER D:</div>
                <InsurerNaicField name={insurerD} id={insurerDId} naic={insurerDNaic} onNameChange={setInsurerD} onIdChange={setInsurerDId} onNaicChange={setInsurerDNaic} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER E:</div>
                <InsurerNaicField name={insurerE} id={insurerEId} naic={insurerENaic} onNameChange={setInsurerE} onIdChange={setInsurerEId} onNaicChange={setInsurerENaic} className="masterCert__input masterCert__inputFull" />
                <div className="masterCert__insurerLabel">INSURER F:</div>
                <InsurerNaicField name={insurerF} id={insurerFId} naic={insurerFNaic} onNameChange={setInsurerF} onIdChange={setInsurerFId} onNaicChange={setInsurerFNaic} className="masterCert__input masterCert__inputFull" />
              </div>
            </div>
          </div>
        </div>

        {/* COVERAGES SECTION */}
        <div className="masterCert__coveragesSection">
          <div className="masterCert__coveragesTitle">COVERAGES</div>
          <div className="masterCert__certNumberGrid">
            <div>
              <div className="masterCert__contactLabel">CERTIFICATE NUMBER:</div>
              <InputText maxLength={30} value={certNumber} onChange={(e) => setCertNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
            <div>
              <div className="masterCert__contactLabel">REVISION NUMBER:</div>
              <InputText maxLength={30} value={revisionNumber} onChange={(e) => setRevisionNumber(e.target.value)} className="masterCert__input masterCert__inputFull" />
            </div>
          </div>

          {/* Coverage Table Header */}
          <div className="masterCert__coverageHeader">
            <div>INSR<br/>LTR</div>
            <div>TYPE OF INSURANCE</div>
            <div>POLICY NUMBER</div>
            <div className="masterCert__coverageHeaderCenter">POLICY EFF<br/>DATE (MM/DD/YY)</div>
            <div className="masterCert__coverageHeaderCenter">POLICY EXP<br/>DATE (MM/DD/YY)</div>
            <div>LIMITS</div>
          </div>

          {/* COMMERCIAL GENERAL LIABILITY */}
          <div className="masterCert__coverageRow">
            <div className="masterCert__coverageInsr">
              <Dropdown value={cglInsurerLtr} onChange={(e) => setCglInsurerLtr(e.value)} options={['A','B','C','D','E','F'].map(l => ({ label: l, value: l }))} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__coverageType">COMMERCIAL GENERAL LIABILITY</div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="cglClaimsMade" checked={cglClaimsMade} onChange={(e) => setCglClaimsMade(e.checked ?? false)} />
                <label htmlFor="cglClaimsMade" className="masterCert__checkboxItemLabel">CLAIMS MADE</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="cglOccur" checked={cglOccur} onChange={(e) => setCglOccur(e.checked ?? false)} />
                <label htmlFor="cglOccur" className="masterCert__checkboxItemLabel">OCCUR</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="cglBlank1" checked={cglBlank1Check} onChange={(e) => setCglBlank1Check(e.checked ?? false)} />
                <InputText value={cglBlank1Text} onChange={(e) => setCglBlank1Text(e.target.value)} className="masterCert__input masterCert__inputSm" />
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="cglBlank2" checked={cglBlank2Check} onChange={(e) => setCglBlank2Check(e.checked ?? false)} />
                <InputText value={cglBlank2Text} onChange={(e) => setCglBlank2Text(e.target.value)} className="masterCert__input masterCert__inputSm" />
              </div>
              <div className="masterCert__genAggLabel">GEN'L AGGREGATE LIMIT APPLIES PER:</div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="cglPolicy" checked={cglPolicy} onChange={(e) => setCglPolicy(e.checked ?? false)} />
                <label htmlFor="cglPolicy" className="masterCert__checkboxItemLabel">POLICY</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="cglProject" checked={cglProject} onChange={(e) => setCglProject(e.checked ?? false)} />
                <label htmlFor="cglProject" className="masterCert__checkboxItemLabel">PROJECT</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="cglLoc" checked={cglLoc} onChange={(e) => setCglLoc(e.checked ?? false)} />
                <label htmlFor="cglLoc" className="masterCert__checkboxItemLabel">LOC</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="cglOther" checked={cglOther} onChange={(e) => setCglOther(e.checked ?? false)} />
                <label htmlFor="cglOther" className="masterCert__checkboxItemLabel">OTHER:</label>
                <InputText value={cglOtherText} onChange={(e) => setCglOtherText(e.target.value)} className="masterCert__input masterCert__inputXs" />
              </div>
              <div className="masterCert__checkboxItem" style={{ marginTop: 6 }}>
                <Checkbox inputId="cglAddlInsd" checked={cglAddlInsd} onChange={(e) => setCglAddlInsd(e.checked ?? false)} />
                <label htmlFor="cglAddlInsd" className="masterCert__checkboxItemLabel">ADDL INSD</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="cglSbrWvd" checked={cglSbrWvd} onChange={(e) => setCglSbrWvd(e.checked ?? false)} />
                <label htmlFor="cglSbrWvd" className="masterCert__checkboxItemLabel">SBR WVD</label>
              </div>
            </div>
            <InputText maxLength={50} value={cglPolicyNumber} onChange={(e) => setCglPolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={cglEffDate} onChange={(e) => setCglEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={cglExpDate} onChange={(e) => setCglExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__limitsGrid">
              <div className="masterCert__limitLabel">EACH OCCURRENCE</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={cglEachOccurrence} onChange={(e) => setCglEachOccurrence(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">DAMAGE TO RENTED PREMISES (Ea occurrence)</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={cglDamageRented} onChange={(e) => setCglDamageRented(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">MED EXP (Any one person)</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={cglMedExp} onChange={(e) => setCglMedExp(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">PERSONAL & ADV INJURY</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={cglPersonalAdv} onChange={(e) => setCglPersonalAdv(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">GENERAL AGGREGATE</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={cglGeneralAgg} onChange={(e) => setCglGeneralAgg(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">PRODUCTS - COMP/OP AGG</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={cglProductsAgg} onChange={(e) => setCglProductsAgg(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <InputText value={cglBlankLimitLabel} onChange={(e) => setCglBlankLimitLabel(e.target.value)} placeholder="" className="masterCert__input masterCert__limitInput" />
              <span className="masterCert__limitDollar">$</span>
              <InputText value={cglBlankLimitAmt} onChange={(e) => setCglBlankLimitAmt(e.target.value)} placeholder="" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* AUTOMOBILE LIABILITY */}
          <div className="masterCert__coverageRow">
            <div className="masterCert__coverageInsr">
              <Dropdown value={autoInsurerLtr} onChange={(e) => setAutoInsurerLtr(e.value)} options={['A','B','C','D','E','F'].map(l => ({ label: l, value: l }))} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__coverageType">AUTOMOBILE LIABILITY</div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="autoAnyAuto" checked={autoAnyAuto} onChange={(e) => setAutoAnyAuto(e.checked ?? false)} />
                <label htmlFor="autoAnyAuto" className="masterCert__checkboxItemLabel">ANY AUTO</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="autoAllOwned" checked={autoAllOwned} onChange={(e) => setAutoAllOwned(e.checked ?? false)} />
                <label htmlFor="autoAllOwned" className="masterCert__checkboxItemLabel">OWNED AUTOS ONLY</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="autoScheduled" checked={autoScheduled} onChange={(e) => setAutoScheduled(e.checked ?? false)} />
                <label htmlFor="autoScheduled" className="masterCert__checkboxItemLabel">SCHEDULED AUTOS</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="autoHired" checked={autoHired} onChange={(e) => setAutoHired(e.checked ?? false)} />
                <label htmlFor="autoHired" className="masterCert__checkboxItemLabel">HIRED AUTOS ONLY</label>
              </div>
              <div className="masterCert__checkboxItem" style={{ marginBottom: 4 }}>
                <Checkbox inputId="autoNonOwned" checked={autoNonOwned} onChange={(e) => setAutoNonOwned(e.checked ?? false)} />
                <label htmlFor="autoNonOwned" className="masterCert__checkboxItemLabel">NON-OWNED AUTOS ONLY</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="autoBlank1" checked={autoBlank1Check} onChange={(e) => setAutoBlank1Check(e.checked ?? false)} />
                <InputText value={autoBlank1Text} onChange={(e) => setAutoBlank1Text(e.target.value)} placeholder="" className="masterCert__input masterCert__inputSm" />
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="autoBlank2" checked={autoBlank2Check} onChange={(e) => setAutoBlank2Check(e.checked ?? false)} />
                <InputText value={autoBlank2Text} onChange={(e) => setAutoBlank2Text(e.target.value)} placeholder="" className="masterCert__input masterCert__inputSm" />
              </div>
              <div className="masterCert__checkboxItem" style={{ marginTop: 6 }}>
                <Checkbox inputId="autoAddlInsd" checked={autoAddlInsd} onChange={(e) => setAutoAddlInsd(e.checked ?? false)} />
                <label htmlFor="autoAddlInsd" className="masterCert__checkboxItemLabel">ADDL INSD</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="autoSbrWvd" checked={autoSbrWvd} onChange={(e) => setAutoSbrWvd(e.checked ?? false)} />
                <label htmlFor="autoSbrWvd" className="masterCert__checkboxItemLabel">SBR WVD</label>
              </div>
            </div>
            <InputText maxLength={50} value={autoPolicyNumber} onChange={(e) => setAutoPolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={autoEffDate} onChange={(e) => setAutoEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={autoExpDate} onChange={(e) => setAutoExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__limitsGrid">
              <div className="masterCert__limitLabel">COMBINED SINGLE LIMIT (Ea accident)</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={autoCombinedSingle} onChange={(e) => setAutoCombinedSingle(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">BODILY INJURY (Per person)</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={autoBodilyPerson} onChange={(e) => setAutoBodilyPerson(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">BODILY INJURY (Per accident)</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={autoBodilyAccident} onChange={(e) => setAutoBodilyAccident(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">PROPERTY DAMAGE (Per accident)</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={autoPropertyDamage} onChange={(e) => setAutoPropertyDamage(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <InputText value={autoBlankLimitLabel} onChange={(e) => setAutoBlankLimitLabel(e.target.value)} placeholder="" className="masterCert__input masterCert__limitInput" />
              <span className="masterCert__limitDollar">$</span>
              <InputText value={autoBlankLimitAmt} onChange={(e) => setAutoBlankLimitAmt(e.target.value)} placeholder="" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* UMBRELLA / EXCESS LIABILITY */}
          <div className="masterCert__coverageRow">
            <div className="masterCert__coverageInsr">
              <Dropdown value={umbrellaInsurerLtr} onChange={(e) => setUmbrellaInsurerLtr(e.value)} options={['A','B','C','D','E','F'].map(l => ({ label: l, value: l }))} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="umbrellaLiab" checked={umbrellaLiab} onChange={(e) => setUmbrellaLiab(e.checked ?? false)} />
                <label htmlFor="umbrellaLiab" className="masterCert__coverageType" style={{ marginLeft: 4 }}>UMBRELLA LIAB</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="umbrellaOccur" checked={umbrellaOccur} onChange={(e) => setUmbrellaOccur(e.checked ?? false)} />
                <label htmlFor="umbrellaOccur" className="masterCert__checkboxItemLabel">OCCUR</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="excessLiab" checked={excessLiab} onChange={(e) => setExcessLiab(e.checked ?? false)} />
                <label htmlFor="excessLiab" className="masterCert__coverageType" style={{ marginLeft: 4 }}>EXCESS LIAB</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="umbrellaClaimsMade" checked={umbrellaClaimsMade} onChange={(e) => setUmbrellaClaimsMade(e.checked ?? false)} />
                <label htmlFor="umbrellaClaimsMade" className="masterCert__checkboxItemLabel">CLAIMS MADE</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="umbrellaDed" checked={umbrellaDed} onChange={(e) => setUmbrellaDed(e.checked ?? false)} />
                <label htmlFor="umbrellaDed" className="masterCert__checkboxItemLabel">DED</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="umbrellaRetentionChk" checked={umbrellaRetentionCheck} onChange={(e) => setUmbrellaRetentionCheck(e.checked ?? false)} />
                <label className="masterCert__checkboxItemLabel">RETENTION $</label>
                <InputText value={umbrellaRetention} onChange={(e) => setUmbrellaRetention(e.target.value)} className="masterCert__input masterCert__inputXs" />
              </div>
              <div className="masterCert__checkboxItem" style={{ marginTop: 6 }}>
                <Checkbox inputId="umbrellaAddlInsd" checked={umbrellaAddlInsd} onChange={(e) => setUmbrellaAddlInsd(e.checked ?? false)} />
                <label htmlFor="umbrellaAddlInsd" className="masterCert__checkboxItemLabel">ADDL INSD</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="umbrellaSbrWvd" checked={umbrellaSbrWvd} onChange={(e) => setUmbrellaSbrWvd(e.checked ?? false)} />
                <label htmlFor="umbrellaSbrWvd" className="masterCert__checkboxItemLabel">SBR WVD</label>
              </div>
            </div>
            <InputText maxLength={50} value={umbrellaPolicyNumber} onChange={(e) => setUmbrellaPolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={umbrellaEffDate} onChange={(e) => setUmbrellaEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={umbrellaExpDate} onChange={(e) => setUmbrellaExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__limitsGrid">
              <div className="masterCert__limitLabel">EACH OCCURRENCE</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={umbrellaEachOccurrence} onChange={(e) => setUmbrellaEachOccurrence(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">AGGREGATE</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={umbrellaAggregate} onChange={(e) => setUmbrellaAggregate(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <InputText value={umbrellaBlankLimitLabel} onChange={(e) => setUmbrellaBlankLimitLabel(e.target.value)} placeholder="" className="masterCert__input masterCert__limitInput" />
              <span className="masterCert__limitDollar">$</span>
              <InputText value={umbrellaBlankLimitAmt} onChange={(e) => setUmbrellaBlankLimitAmt(e.target.value)} placeholder="" className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* WORKERS COMPENSATION & EMPLOYERS LIABILITY */}
          <div className="masterCert__coverageRow">
            <div className="masterCert__coverageInsr">
              <Dropdown value={wcInsurerLtr} onChange={(e) => setWcInsurerLtr(e.value)} options={['A','B','C','D','E','F'].map(l => ({ label: l, value: l }))} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__coverageType">WORKERS COMPENSATION AND EMPLOYERS' LIABILITY</div>
              <div className="masterCert__wcNote">ANY PROPRIETOR / PARTNER/EXECUTIVE OFFICER / MEMBER EXCLUDED?</div>
              <div className="masterCert__wcNote">If yes, describe under SPECIAL PROVISIONS below</div>
              <InputText maxLength={2000} value={wcSpecialProvisions} onChange={(e) => setWcSpecialProvisions(e.target.value)} className="masterCert__input" style={{ width: 40 }} />
              <div className="masterCert__checkboxItem" style={{ marginTop: 6 }}>
                <Checkbox inputId="wcAddlInsd" checked={wcAddlInsd} onChange={(e) => setWcAddlInsd(e.checked ?? false)} />
                <label htmlFor="wcAddlInsd" className="masterCert__checkboxItemLabel">ADDL INSD</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="wcSbrWvd" checked={wcSbrWvd} onChange={(e) => setWcSbrWvd(e.checked ?? false)} />
                <label htmlFor="wcSbrWvd" className="masterCert__checkboxItemLabel">SBR WVD</label>
              </div>
            </div>
            <InputText maxLength={50} value={wcPolicyNumber} onChange={(e) => setWcPolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={wcEffDate} onChange={(e) => setWcEffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={wcExpDate} onChange={(e) => setWcExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__limitsGrid">
              <div className="masterCert__wcStatutory">
                <Checkbox inputId="wcPerStatute" checked={wcPerStatute} onChange={(e) => setWcPerStatute(e.checked ?? false)} />
                <label htmlFor="wcPerStatute" className="masterCert__checkboxItemLabel">PER STATUTE</label>
              </div>
              <span />
              <InputText className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__wcStatutory">
                <Checkbox inputId="wcOther" checked={wcOther} onChange={(e) => setWcOther(e.checked ?? false)} />
                <label htmlFor="wcOther" className="masterCert__checkboxItemLabel">OTHER</label>
              </div>
              <span />
              <InputText value={wcOtherText} onChange={(e) => setWcOtherText(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">E.L. EACH ACCIDENT</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={wcEachAccident} onChange={(e) => setWcEachAccident(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">E.L. DISEASE - EA EMPLOYEE</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={wcDiseaseEmployee} onChange={(e) => setWcDiseaseEmployee(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <div className="masterCert__limitLabel">E.L. DISEASE - POLICY LIMIT</div>
              <span className="masterCert__limitDollar">$</span>
              <InputText value={wcDiseasePolicyLimit} onChange={(e) => setWcDiseasePolicyLimit(e.target.value)} className="masterCert__input masterCert__limitInput" />
            </div>
          </div>

          {/* OTHER COVERAGE 1 */}
          <div className="masterCert__coverageRow">
            <div className="masterCert__coverageInsr">
              <Dropdown value={other1InsurerLtr} onChange={(e) => setOther1InsurerLtr(e.value)} options={['A','B','C','D','E','F'].map(l => ({ label: l, value: l }))} placeholder="" className="masterCert__input" emptyMessage="No available options" />
            </div>
            <div>
              <div className="masterCert__coverageType">OTHER</div>
              <InputTextarea value={other1Type} onChange={(e) => setOther1Type(e.target.value)} placeholder="Type of insurance" rows={3} className="masterCert__input masterCert__inputFull" autoResize />
              <div className="masterCert__checkboxItem" style={{ marginTop: 6 }}>
                <Checkbox inputId="other1AddlInsd" checked={other1AddlInsd} onChange={(e) => setOther1AddlInsd(e.checked ?? false)} />
                <label htmlFor="other1AddlInsd" className="masterCert__checkboxItemLabel">ADDL INSD</label>
              </div>
              <div className="masterCert__checkboxItem">
                <Checkbox inputId="other1SbrWvd" checked={other1SbrWvd} onChange={(e) => setOther1SbrWvd(e.checked ?? false)} />
                <label htmlFor="other1SbrWvd" className="masterCert__checkboxItemLabel">SBR WVD</label>
              </div>
            </div>
            <InputText maxLength={50} value={other1PolicyNumber} onChange={(e) => setOther1PolicyNumber(e.target.value)} placeholder={noPolicyPlaceholder} className="masterCert__input masterCert__inputFull" />
            <div className="masterCert__dateCell">
              <Calendar value={other1EffDate} onChange={(e) => setOther1EffDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__dateCell">
              <Calendar value={other1ExpDate} onChange={(e) => setOther1ExpDate(e.value as Date)} className="masterCert__input" showIcon dateFormat="mm/dd/yy" />
            </div>
            <div className="masterCert__otherLimitsGrid">
              <InputText value={other1LimitLabel1} onChange={(e) => setOther1LimitLabel1(e.target.value)} className="masterCert__input masterCert__otherLimitLabel" />
              <span className="masterCert__limitDollar">$</span>
              <InputText value={other1LimitAmt1} onChange={(e) => setOther1LimitAmt1(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <InputText value={other1LimitLabel2} onChange={(e) => setOther1LimitLabel2(e.target.value)} className="masterCert__input masterCert__otherLimitLabel" />
              <span className="masterCert__limitDollar">$</span>
              <InputText value={other1LimitAmt2} onChange={(e) => setOther1LimitAmt2(e.target.value)} className="masterCert__input masterCert__limitInput" />
              <InputText value={other1LimitLabel3} onChange={(e) => setOther1LimitLabel3(e.target.value)} className="masterCert__input masterCert__otherLimitLabel" />
              <span className="masterCert__limitDollar">$</span>
              <InputText value={other1LimitAmt3} onChange={(e) => setOther1LimitAmt3(e.target.value)} className="masterCert__input masterCert__limitInput" />
            </div>
          </div>
        </div>

        {/* DESCRIPTION OF OPERATIONS */}
        <div className="masterCert__descriptionSection">
          <div className="masterCert__descriptionTitle">DESCRIPTION OF OPERATIONS / LOCATIONS / VEHICLES / EXCLUSIONS ADDED BY ENDORSEMENT / SPECIAL PROVISIONS</div>
          <InputTextarea maxLength={2000} value={descriptionOfOps} onChange={(e) => setDescriptionOfOps(e.target.value)} rows={4} className="masterCert__descriptionTextarea" />
        </div>

        {/* CERTIFICATE HOLDER & CANCELLATION */}
        <div className="masterCert__bottomRow">
          <div className="masterCert__certHolderBox" style={!holderEnabled ? { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', minHeight: 140 } : {}}>
            {!holderEnabled ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                <div className="masterCert__sectionHeader" style={{ marginBottom: 0, color: '#9ca3af' }}>CERTIFICATE HOLDER</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '11px', color: '#9ca3af' }}>
                  <i className="pi pi-info-circle" style={{ fontSize: '11px' }} />
                  <span>Certificate holder can be added while sending certificate.</span>
                </div>
                <Button
                  label="Enable"
                  icon="pi pi-lock-open"
                  size="small"
                  outlined
                  style={{ height: '28px', fontSize: '11px', padding: '0 14px', borderRadius: '4px', fontWeight: 500 }}
                  onClick={() => setHolderEnabled(true)}
                />
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div className="masterCert__sectionHeader" style={{ marginBottom: 0 }}>CERTIFICATE HOLDER</div>
                  <Button
                    label="Disable"
                    icon="pi pi-lock"
                    size="small"
                    style={{ height: '22px', fontSize: '10px', padding: '0 8px', borderRadius: '4px', fontWeight: 500 }}
                    onClick={() => { setHolderEnabled(false); setHolderName(''); setHolderAddress(''); setHolderCity(''); setHolderStreet(''); setHolderState(''); setHolderZip(''); setHolderSearchQuery(''); }}
                  />
                </div>
                <div style={{ position: 'relative', width: '100%', marginBottom: 6 }}>
                  <i className="pi pi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 13, zIndex: 1, pointerEvents: 'none' }} />
                  <AutoComplete
                    value={holderSearchQuery}
                    suggestions={holderSuggestions}
                    completeMethod={(e) => { setHolderSearchQuery(e.query); fetchHolderSuggestions(e.query); }}
                    onChange={(e) => { if (typeof e.value === 'string') setHolderSearchQuery(e.value); }}
                    field="label"
                    placeholder="Search for a certificate holder"
                    style={{ width: '100%' }}
                    inputStyle={{ width: '100%', paddingLeft: 30 }}
                    itemTemplate={(item: any) => {
                      if (!item) return null;
                      const r = item.value || {};
                      const location = [r.city, r.stateName].filter(Boolean).join(', ');
                      const address = [r.addressLine1, location, r.zipCode].filter(Boolean).join(' · ');
                      const aiDetails: any[] = (r.additionalInsuredDetails ?? [])
                        .filter((ai: any) => ai.truckingCompanyId === insuredId);
                      const isAdditionalInsured = aiDetails.length > 0 && (
                        selectedPolicies.length === 0 ||
                        aiDetails.some((ai: any) =>
                          (ai.policies ?? []).map((p: any) => p.databaseId as string)
                            .some((pid: string) => selectedPolicies.includes(pid))
                        )
                      );
                      return (
                        <div className="custom-ac-item custom-ac-item--rich">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div className="custom-ac-item__name">{item.label}</div>
                            {isAdditionalInsured && (
                              <span style={{ fontSize: 10, fontWeight: 600, color: '#0057A4', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap', lineHeight: '16px' }}>
                                Additional Insured
                              </span>
                            )}
                          </div>
                          {address && <div className="custom-ac-item__address">{address}</div>}
                        </div>
                      );
                    }}
                    onSelect={(e) => {
                      const h = e.value.value;
                      setHolderName(h.name || '');
                      setHolderAddress(h.addressLine1 || '');
                      setHolderStreet(h.addressLine2 || '');
                      setHolderCity(h.city || '');
                      setHolderState(h.stateName || '');
                      setHolderZip(h.zipCode || '');
                      setHolderSearchQuery('');
                    }}
                  />
                </div>
                <InputText maxLength={100} value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Name" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
                <InputText maxLength={100} value={holderAddress} onChange={(e) => setHolderAddress(e.target.value)} placeholder="Address" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
                <InputText maxLength={100} value={holderStreet} onChange={(e) => setHolderStreet(e.target.value)} placeholder="Address Line 1" className="masterCert__input masterCert__inputFull masterCert__inputMbSm" />
                <div className="masterCert__addressGrid">
                  <InputText maxLength={100} value={holderCity} onChange={(e) => setHolderCity(e.target.value)} placeholder="City" className="masterCert__input masterCert__inputFull" />
                  <InputText maxLength={50} value={holderState} onChange={(e) => setHolderState(e.target.value)} placeholder="State" className="masterCert__input masterCert__inputFull" />
                  <InputText maxLength={20} value={holderZip} onChange={(e) => setHolderZip(e.target.value)} placeholder="Zip" className="masterCert__input masterCert__inputFull" />
                </div>
              </>
            )}
          </div>
          <div className="masterCert__cancellationBox">
            <div className="masterCert__sectionHeader">CANCELLATION</div>
            <div className="masterCert__cancellationText">
              Should any of the above described policies be cancelled before the expiration date thereof, notice will be delivered in accordance with the policy provisions.
            </div>
            <div className="masterCert__authRepLabel">AUTHORIZED REPRESENTATIVE</div>
            <div className="masterCert__authRepRow">
              <Dropdown value={authRepSignature} onChange={(e) => setAuthRepSignature(e.value)} options={signatureOptions} optionLabel="label" optionValue="value" placeholder="Select signature:" className="masterCert__input masterCert__authRepDropdown" emptyMessage="No available options" />
              <Button
                label="Create New"
                className="p-button-outlined carrierDialog__secondaryBtn"
                onClick={() => setShowAddSignature(true)}
              />
            </div>
              {signaturePreviewUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={signaturePreviewUrl} alt="Signature preview" style={{ maxHeight: 60, maxWidth: 280, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', background: '#fff' }} />
                </div>
              )}
          </div>
        </div>

        {/* ACORD Footer */}
        <div className="masterCert__acordFooter">
          <div>ACORD 25 (2016/03)</div>
          <div>&copy; 1988-2015 ACORD CORPORATION. All rights reserved.</div>
        </div>

        {/* ── ACORD 101 - Additional Remarks Schedule ── */}
        {showAcord101 && (
          <div className="masterCert__acord101">
            {/* ACORD 101 Header */}
            <div className="masterCert__acordHeader" style={{ marginTop: 24 }}>
              <img src={acordLogo} alt="ACORD" className="masterCert__acordLogo" />
              <div className="masterCert__acordTitleWrapper">
                <div className="masterCert__acordTitle">ADDITIONAL REMARKS SCHEDULE</div>
              </div>
            </div>

            <div className="masterCert__twoColumnLayout">
              <div>
                <div className="masterCert__sectionHeader">AGENCY</div>
                <InputTextarea maxLength={250}
                  value={acord101Agency}
                  onChange={(e) => setAcord101Agency(e.target.value)}
                  rows={3}
                  className="masterCert__descriptionTextarea"
                  placeholder="Agency name and address"
                />
              </div>
              <div>
                <div className="masterCert__sectionHeader">NAMED INSURED</div>
                <InputTextarea maxLength={100}
                  value={acord101NamedInsured}
                  onChange={(e) => setAcord101NamedInsured(e.target.value)}
                  rows={3}
                  className="masterCert__descriptionTextarea"
                  placeholder="Named insured"
                />
              </div>
            </div>

            <div className="masterCert__descriptionSection">
              <div className="masterCert__descriptionTitle">ADDITIONAL REMARKS</div>
              <InputTextarea maxLength={2000}
                value={acord101AdditionalRemarks}
                onChange={(e) => setAcord101AdditionalRemarks(e.target.value)}
                rows={8}
                className="masterCert__descriptionTextarea"
                placeholder="Enter additional remarks..."
              />
            </div>

            <div className="masterCert__acordFooter">
              <div>ACORD 101 (2008/01)</div>
              <div>&copy; 2008 ACORD CORPORATION. All rights reserved.</div>
            </div>
          </div>
        )}
      </div>

      <CreateNewCarriers open={showCreateCarrier} onClose={() => setShowCreateCarrier(false)} />
      <AddNewSignature open={showAddSignature} onClose={() => setShowAddSignature(false)} onSaved={refetchSignatures} />
    </>
  );
});

export default Acord25Form;
